/* eslint-disable react/react-in-jsx-scope */
import { history } from 'duck/store'
import { TemporalEntityId } from 'entities-next/common'
import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import { LoadingScreen } from 'scenes/Dashboard/components-next/Loading'
import { EntityId } from 'scenes/Dashboard/data/common/types'
import { USER_MANAGER_LIST_PATH } from 'scenes/Dashboard/scenes/UsersManager'
import { ExtendableStyles, Testable } from 'utils-next/types'

import { CorporateStatus, getApiCorporateHandlers } from '../../../../../../../../entities-next/corporate'
import { ApiUserData, getApiUserHandlers, USER_CACHE_STORE_ID } from '../../../../../../../../entities-next/user'
import { UserDataForm } from '../../components/UserDataForm/UserDataForm.component'
import { useUserDetailsViewStore } from '../../UserDetailsView.store'
import {
  failedUserOperationAction,
  successfulUserCreationAction,
  successfulUserUpdateAction,
} from './UserDetailsFormViewAlerts.component'

export type UserDetailsFormViewProps = ExtendableStyles & Testable

export function UserDetailsFormView({ className, style, ...otherProps }: UserDetailsFormViewProps) {
  const {
    state: { user, status, actionBarRef },
  } = useUserDetailsViewStore()
  const { updateOrCreateUser } = useUserDetailsFormViewHandlers()

  if (!user || status === 'loading') {
    return <LoadingScreen />
  }

  return <UserDataForm user={user} onSubmit={updateOrCreateUser} actionBarRef={actionBarRef} {...otherProps} />
}

function useUserDetailsFormViewHandlers() {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const updateOrCreateUser = useCallback(
    async (userId: EntityId, userData: ApiUserData) => {
      const { apiGetCorporate, apiCreateCorporate } = getApiCorporateHandlers()
      const { apiUpdateUser, apiCreateUser } = getApiUserHandlers()
      if (userId === TemporalEntityId.new) {
        const userToCreate = { ...userData }
        let corporate
        if (userData.corporateId) {
          try {
            corporate = await apiGetCorporate(userData.corporateId)
            userToCreate.corporateId = corporate.id
          } catch (e) {
            dispatch(failedUserOperationAction(new Error(`Failed to create User: ${e.message}`)))
          }
        } else {
          try {
            corporate = await apiCreateCorporate({
              name: `Corporate - ${userData.firstName} ${userData.lastName}`,
              demo: false,
              enabledFeatures: [],
              status: CorporateStatus.active,
              seatsLimit: 999,
            })
            userToCreate.corporateId = corporate.id
          } catch (e) {
            dispatch(failedUserOperationAction(new Error(`Failed to create User": ${e.message}`)))
          }
        }
        if (corporate && userToCreate.corporateId) {
          try {
            await apiCreateUser(userToCreate)
            dispatch(successfulUserCreationAction(userData))
            history.push(USER_MANAGER_LIST_PATH)
            queryClient.refetchQueries(USER_CACHE_STORE_ID)
          } catch (e) {
            dispatch(failedUserOperationAction(new Error(`Failed to create User: ${e.message}`)))
          }
        }
      } else {
        try {
          await apiUpdateUser(userId, userData)
          dispatch(successfulUserUpdateAction(userData))
          history.push(USER_MANAGER_LIST_PATH)
          queryClient.refetchQueries(USER_CACHE_STORE_ID)
        } catch (e) {
          dispatch(failedUserOperationAction(new Error(`Failed to update User: ${e.message}`)))
        }
      }
    },
    [dispatch]
  )

  return {
    updateOrCreateUser,
  }
}
